/**
 * Line-item unit vocabulary and derived-weight maths.
 *
 * The four units a line can carry are the keys of STRINGS.common.unitOptions:
 *
 *   kg   weight in kilograms
 *   m    length in metres
 *   mts  weight in metric tonnes (1 MTS = 1000 kg)
 *   pcs  a raw piece count
 *
 * Those keys are the values persisted to the backend, and they are the
 * CANONICAL forms: app/utils/units.py normalize_unit() rewrites every unit on
 * write ("meters" -> "m", "pieces" -> "pcs"), so a saved line comes back short.
 * The long spellings are still accepted on read for rows that predate that.
 *
 * CAREFUL: "mtr"/"mtrs" are metres; "mts" is metric tonnes. One character
 * apart, quantities 1000x apart.
 *
 * This module exists because the same weight ladder had been copy-pasted into
 * five React forms and had already drifted between them.
 */

export const KG_PER_TONNE = 1000;

export const isLengthUnit = (u) => u === 'm' || u === 'meters' || u === 'mtr' || u === 'mtrs';
export const isTonneUnit = (u) => u === 'mts';
export const isPieceUnit = (u) => u === 'pcs' || u === 'pieces';
/** Weight-denominated: the quantity itself expresses mass. */
export const isWeightUnit = (u) => u === 'kg' || isTonneUnit(u);

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Physical weight of a line in KILOGRAMS.
 *
 * Mirrors app/utils/units.py line_weight_kg so the preview a user sees matches
 * what the backend will compute:
 *
 *   kg   -> quantity
 *   mts  -> quantity * 1000
 *   m    -> (product_weight / standard_length) * quantity
 *   pcs  -> effective_length * product_weight * quantity / standard_length
 *
 * Returns 0 rather than NaN/Infinity for missing or zero product specs, so a
 * half-filled row never renders "NaN kg".
 */
export const lineWeightKg = ({ unit, quantity, length, productWeight, standardLength }) => {
  const qty = num(quantity);
  const pWeight = num(productWeight);
  const sLength = num(standardLength);
  const len = num(length);

  if (unit === 'kg') return qty;
  if (isTonneUnit(unit)) return qty * KG_PER_TONNE;

  if (isPieceUnit(unit)) {
    if (sLength <= 0) return 0;
    const effectiveLen = len > 0 ? len : sLength;
    return (effectiveLen * pWeight * qty) / sLength;
  }

  if (isLengthUnit(unit)) {
    if (sLength <= 0) return 0;
    return (pWeight / sLength) * qty;
  }

  return 0;
};

/**
 * Per-piece measure shown beside a line — unit-dependent:
 *   m / metres -> standard_length (length per piece)
 *   everything else -> product_weight (kg per piece)
 * Pieces get nothing: a per-piece weight is not meaningful when the quantity
 * IS the piece count.
 */
export const perPieceMeasure = ({ unit, productWeight, standardLength }) => {
  if (isPieceUnit(unit)) return null;
  if (isLengthUnit(unit)) {
    const sLen = num(standardLength);
    return sLen > 0 ? sLen : null;
  }
  const w = num(productWeight);
  return w > 0 ? w : null;
};

/**
 * Approximate quantity derived from a piece count, expressed in the line's own
 * unit (NOT kilograms) — it is a preview of the editable Quantity field.
 *   kg   -> pieces * product_weight
 *   mts  -> pieces * product_weight / 1000
 *   m    -> pieces * standard_length
 *   pcs  -> pieces (1:1)
 */
export const approxQtyFromPieces = ({ unit, pieces, productWeight, standardLength }) => {
  const pcs = num(pieces);
  if (pcs <= 0) return null;

  if (isPieceUnit(unit)) return pcs;

  if (isLengthUnit(unit)) {
    const sLen = num(standardLength);
    return sLen > 0 ? pcs * sLen : null;
  }

  const w = num(productWeight);
  if (w <= 0) return null;
  const kg = pcs * w;
  return isTonneUnit(unit) ? kg / KG_PER_TONNE : kg;
};

/**
 * Decimal places on a line quantity. Matches the backend cap, which matches
 * the NUMERIC(10,3) column — tightening it would make existing 3dp rows fail
 * validation and become uneditable. 3dp also gives a tonne line exactly 1 kg
 * of resolution (0.001 MTS = 1 kg).
 */
export const MAX_QTY_DECIMALS = 3;

/** Display precision for the read-only derived columns. */
export const fmtQty = (n) => (n === null || n === undefined || !Number.isFinite(Number(n)))
  ? ''
  : String(Number(Number(n).toFixed(MAX_QTY_DECIMALS)));
